import {
  AnalyticsPageName,
  BacktestingScoreBreakdown,
  HelperTooltipWithTranslations,
  LanguageNameSpaces,
  OnlyHelperTooltipProps,
  SubGroupReviewRejectionReason,
  TagName,
} from "@shared/types";
import {
  GroupsSort,
  RiskType,
  ScoreSection,
} from "@shared/features/newMarketplace/types";
import { TranslationKeys } from "@shared/common/i18n/types";

export const KEYS_TO_DISPLAY_TEXT: Record<
  keyof BacktestingScoreBreakdown,
  TranslationKeys<LanguageNameSpaces.MARKETPLACE>
> = {
  pnl: "score.metrics.pnl",
  beats_the_market: "score.metrics.beats_the_market",
  winning_losing: "score.metrics.winning_losing",
  sharpe_ratio: "score.metrics.sharpe_ratio",
  sortino_ratio: "score.metrics.sortino_ratio",
  low_leverage: "score.metrics.low_leverage",
  max_drawdown: "score.metrics.max_drawdown",
  avg_trade_time: "score.metrics.avg_trade_time",
  has_stop_loss: "score.metrics.has_stop_loss",
  pnl_standard_deviation: "score.metrics.pnl_standard_deviation",
  avg_rating: "score.metrics.avg_rating",
  is_free_or_has_free_trial: "score.metrics.is_free_or_has_free_trial",
  age: "score.metrics.age",
  number_of_trades: "score.metrics.number_of_trades",
};

export const SCORE_SECTION_TO_COLOR = {
  [ScoreSection.PERFORMANCE]: { "0%": "#D9E5FF", "100%": "#3272FE" },
  [ScoreSection.RISK]: { "0%": "#FFE9AF", "100%": "#FFC146" },
  [ScoreSection.SERVICE]: { "0%": "#B1EDFF", "100%": "#60AFE8" },
};

export const SCORE_SECTION_TO_DARK_MODE_COLOR = {
  [ScoreSection.PERFORMANCE]: { "0%": "#3556A1", "100%": "#3272FE" },
  [ScoreSection.RISK]: { "0%": "#8E7952", "100%": "#FEB932" },
  [ScoreSection.SERVICE]: { "0%": "#4774A4", "100%": "#6ADAFB" },
};

export const SCORE_SECTION_TO_KEYS: Record<
  ScoreSection,
  (keyof BacktestingScoreBreakdown)[]
> = {
  [ScoreSection.PERFORMANCE]: [
    "pnl",
    "beats_the_market",
    "winning_losing",
    "sharpe_ratio",
    "sortino_ratio",
  ],
  [ScoreSection.RISK]: [
    "low_leverage",
    "max_drawdown",
    "avg_trade_time",
    "has_stop_loss",
    "pnl_standard_deviation",
  ],
  [ScoreSection.SERVICE]: [
    "avg_rating",
    "is_free_or_has_free_trial",
    "age",
    "number_of_trades",
  ],
};

export const REJECTION_REASON_TO_MESSAGE: Record<
  SubGroupReviewRejectionReason,
  TranslationKeys<LanguageNameSpaces.MARKETPLACE>
> = {
  [SubGroupReviewRejectionReason.OBSCENE_LANGUAGE]:
    "reviews.rejection_reasons.obscene_language",
  [SubGroupReviewRejectionReason.MISLEADING_OR_INACCURATE_INFORMATION]:
    "reviews.rejection_reasons.misleading_info",
  [SubGroupReviewRejectionReason.FOCUS_ON_CHANNEL]:
    "reviews.rejection_reasons.focus_on_channel",
  [SubGroupReviewRejectionReason.CONFLICT_OF_INTEREST]:
    "reviews.rejection_reasons.conflict_of_interest",
  [SubGroupReviewRejectionReason.OTHER]: "reviews.rejection_reasons.other",
};

export const SUB_GROUP_CARD_TAGS = [
  TagName.NEW,
  TagName.TOP_SELLER,
  TagName.SALE,
];

export const MARKETPLACE_SORT_TO_PATH: { [sortKey in GroupsSort]?: string } = {
  [GroupsSort.SCORE]: "backtesting_score",
  [GroupsSort.RATING]: "avg_stars",
  [GroupsSort.PRICE]: "price_number",
  [GroupsSort.NAME]: "name",
};

export const MARKETPLACE_STRING_SORTS = [GroupsSort.NAME];

export const MIN_NUM_OF_REVIEWS = 3;

export const GROUP_STATISTICS_DISCLAIMER_ARTICLE = "9728187";
export const MARKETPLACE_REVIEW_GUIDELINES_ARTICLE = "10436159";

export const RISK_TYPE_TO_PERCENT_RANGE = {
  [RiskType.LOW]: [0, 25],
  [RiskType.MEDIUM]: [25, 50],
  [RiskType.HIGH]: [50, 75],
  [RiskType.VERY_HIGH]: [75, 100],
};

export const OPPOSITE_SORTS = [
  GroupsSort.RATING,
  GroupsSort.SCORE,
  GroupsSort.SIX_MONTH_PNL,
  GroupsSort.THREE_MONTH_PNL,
  GroupsSort.ONE_MONTH_PNL,
];

export const SCORE_HELPER_PROPS: HelperTooltipWithTranslations<LanguageNameSpaces.MARKETPLACE> =
  {
    description: "score.helper_descriptions.score",
    analyticsPageName: AnalyticsPageName.MARKETPLACE,
    analyticsFieldName: "Score",
    articleId: "9731166",
    title: "score.title",
  };

export const SCORE_KEYS_TO_HELPER_PROPS: {
  [key in keyof BacktestingScoreBreakdown]?: OnlyHelperTooltipProps;
} = {
  beats_the_market: {
    description: "score.helper_descriptions.beats_the_market",
    articleId: "9731166",
  },
  winning_losing: {
    description: "score.helper_descriptions.winning_losing",
  },
  sharpe_ratio: {
    description: "score.helper_descriptions.sharpe_ratio",
    articleId: "9731166",
  },
  sortino_ratio: {
    description: "score.helper_descriptions.sortino_ratio",
    articleId: "9731166",
  },
  low_leverage: {
    description: "score.helper_descriptions.low_leverage",
  },
  max_drawdown: {
    description: "score.helper_descriptions.max_drawdown",
    articleId: "9731166",
  },
  avg_trade_time: {
    description: "score.helper_descriptions.avg_trade_time",
  },
  has_stop_loss: {
    description: "score.helper_descriptions.has_stop_loss",
  },
  pnl_standard_deviation: {
    description: "score.helper_descriptions.pnl_standard_deviation",
    articleId: "9731166",
  },
  avg_rating: {
    description: "score.helper_descriptions.avg_rating",
    articleId: "9731166",
  },
  is_free_or_has_free_trial: {
    description: "score.helper_descriptions.is_free_or_has_free_trial",
  },
  age: {
    description: "score.helper_descriptions.age",
  },
  number_of_trades: {
    description: "score.helper_descriptions.number_of_trades",
  },
};
